import { withProps } from "@udecode/cn";
import { BasicElementsPlugin } from "@udecode/plate-basic-elements/react";
import {
  BasicMarksPlugin,
  BoldPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
} from "@udecode/plate-basic-marks/react";
import {
  MarkdownPlugin,
  remarkMention,
  remarkMdx,
} from "@udecode/plate-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { HeadingPlugin } from "@udecode/plate-heading/react";
import { HEADING_KEYS } from "@udecode/plate-heading";
import { LinkPlugin } from "@udecode/plate-link/react";
import { linkPlugin } from "./plugins/link-plugin";
import { ResetNodePlugin } from "@udecode/plate-reset-node/react";
import { ListPlugin } from "@udecode/plate-list/react";
import {
  ParagraphPlugin,
  PlateElement,
  PlateLeaf,
  usePlateEditor,
} from "@udecode/plate/react";
import {
  ImagePlugin,
  PlaceholderPlugin,
  MediaEmbedPlugin,
} from "@udecode/plate-media/react";
import { TablePlugin } from "@udecode/plate-table/react";
import { NodeIdPlugin } from "@udecode/plate-node-id";

import { ImageElement } from "./elements/ImageElement";
import { MediaEmbedElement } from "./elements/MediaEmbedElement";
import { LinkElement } from "./elements/LinkElement";
import {
  TableElement,
  TableRowElement,
  TableCellElement,
  TableCellHeaderElement,
} from "./elements/TableElements";
import { TElement } from "@udecode/plate";

export const EditorComponents = {
  blockquote: withProps(PlateElement, {
    as: "blockquote",
    className: "mb-4 border-l-4 border-[#d0d7de] pl-4 text-[#636c76]",
  }),
  [BoldPlugin.key]: withProps(PlateLeaf, { as: "strong" }),
  [ItalicPlugin.key]: withProps(PlateLeaf, { as: "em" }),
  [ParagraphPlugin.key]: withProps(PlateElement, {
    as: "p",
    className: "mb-4",
  }),
  [StrikethroughPlugin.key]: withProps(PlateLeaf, { as: "s" }),
  [UnderlinePlugin.key]: withProps(PlateLeaf, { as: "u" }),
  [HEADING_KEYS.h1]: withProps(PlateElement, {
    as: "h1",
    className: "mb-4 mt-6 text-3xl font-semibold tracking-tight lg:text-4xl",
  }),
  [HEADING_KEYS.h2]: withProps(PlateElement, {
    as: "h2",
    className: "mb-4 mt-6 text-2xl font-semibold tracking-tight",
  }),
  [HEADING_KEYS.h3]: withProps(PlateElement, {
    as: "h3",
    className: "mb-4 mt-6 text-xl font-semibold tracking-tight",
  }),
  [HEADING_KEYS.h4]: withProps(PlateElement, {
    as: "h4",
    className: "mb-4 mt-4 text-lg font-semibold tracking-tight",
  }),
  // List components styling
  ul: withProps(PlateElement, {
    as: "ul",
    className: "list-disc ml-6 mb-4",
  }),
  ol: withProps(PlateElement, {
    as: "ol",
    className: "list-decimal ml-6 mb-4",
  }),
  li: withProps(PlateElement, {
    as: "li",
    className: "mb-1",
  }),
  // Table components using Plate UI elements
  table: TableElement,
  tr: TableRowElement,
  td: TableCellElement,
  th: TableCellHeaderElement,
  // Link styling
  // a: withProps(PlateElement, {
  //   as: "a",
  //   className: "text-blue-600 hover:text-blue-800 hover:underline",
  // }),
  [LinkPlugin.key]: LinkElement,
  [ImagePlugin.key]: withProps(ImageElement, {
    className: "my-4 relative",
  }),
  [PlaceholderPlugin.key]: withProps(PlateElement, {
    as: "div",
    className:
      "relative p-2 border-2 border-dashed border-gray-200 rounded-md my-4",
  }),
  [MediaEmbedPlugin.key]: withProps(MediaEmbedElement, {
    className: "my-4 relative",
  }),
};

export const useCreateEditor = (initialValue: TElement[]) => {
  return usePlateEditor({
    components: { ...EditorComponents },
    plugins: [
      // Basic plugins
      BasicElementsPlugin,
      BasicMarksPlugin,
      HeadingPlugin,

      // List plugin for ordered and unordered lists
      ListPlugin,


      // Link plugin for hyperlinks
      linkPlugin,

      // Reset node plugin (for clearing formatting)
      ResetNodePlugin,

      // Media plugins for handling images and embeds
      ImagePlugin,
      MediaEmbedPlugin,

      // Add PlaceholderPlugin for media uploads
      PlaceholderPlugin.configure({
        options: { disableEmptyPlaceholder: true },
      }),

      // Markdown plugin for handling markdown content
      MarkdownPlugin.configure({
        options: {
          // Add remark plugins for syntax extensions (GFM, Math, MDX)
          remarkPlugins: [remarkMath, remarkGfm, remarkMdx, remarkMention],
          // Define custom rules if needed
          rules: {
            // date: { /* ... rule implementation ... */ },
          },
        },
      }),
      TablePlugin.configure({
        options: {
          initialTableWidth: 600,
        },
      }),
      NodeIdPlugin,
      // Add DnD plugin for drag-and-drop functionality
      // DndPlugin,
    ],
    value: initialValue || [],
  });
};
